// Cooperative, framerate-independent subsystem scheduler.
//
// Presentation subsystems (HUD text, netgraph, name tags, particles, …) don't
// need to run at the render rate. This runs them on their own cadence without
// threads, so a slow subsystem degrades *its own* rate rather than the frame:
//
//   - Each task has a minimum `intervalMs` (0 = every frame) and a soft
//     `budgetMs`.
//   - Each frame the loop calls `run(now, deadline)`. Due tasks execute in
//     registration order; once `deadline` (a `performance.now()` value) is
//     exceeded, the scheduler stops *starting* new tasks and they wait for the
//     next frame. A heavy task can also read the deadline it is handed and
//     resume next frame (amortised work).
//
// This is the browser-side take on Vercidium's "subsystems run at their own
// rate; the main loop never waits on them" idea. Determinism is unaffected:
// tasks are presentation-only and never feed authoritative state.

export interface ScheduledTask {
  /** Unique name (used for timing + the "next run" bookkeeping). */
  readonly name: string;
  /** Minimum ms between runs (0 = every frame). */
  readonly intervalMs: number;
  /** Soft per-run budget (ms) — for accounting/over-budget warnings. */
  readonly budgetMs: number;
  /** Run the task. `deadline` is the `performance.now()` it should aim to yield by. */
  run: (now: number, deadline: number) => void;
}

export class Scheduler {
  private tasks: ScheduledTask[] = [];
  private nextAt = new Map<string, number>();
  /** Last run duration per task (ms). */
  readonly lastMs = new Map<string, number>();
  /** Number of runs that exceeded the task's soft `budgetMs`. */
  readonly overBudget = new Map<string, number>();

  add(task: ScheduledTask): void {
    this.tasks.push(task);
    this.nextAt.set(task.name, 0);
  }

  /**
   * Run all due tasks until `deadline` (`performance.now()` ms). Returns the
   * number that ran. Never runs a task whose `intervalMs` hasn't elapsed.
   */
  run(now: number, deadline: number): number {
    let ran = 0;
    for (const t of this.tasks) {
      if (now < (this.nextAt.get(t.name) ?? 0)) continue;
      if (ran > 0 && performance.now() > deadline) break; // stop starting tasks once over budget
      this.nextAt.set(t.name, now + t.intervalMs);
      const t0 = performance.now();
      t.run(now, deadline);
      const ms = performance.now() - t0;
      this.lastMs.set(t.name, ms);
      if (t.budgetMs > 0 && ms > t.budgetMs) {
        this.overBudget.set(t.name, (this.overBudget.get(t.name) ?? 0) + 1);
      }
      ran++;
    }
    return ran;
  }

  /** Sum of the last run time of every task (ms) — cheap profiler input. */
  totalLastMs(): number {
    let s = 0;
    for (const v of this.lastMs.values()) s += v;
    return s;
  }

  /** One-line summary of the last run times (for `log()` / debugging). */
  report(): string {
    return this.tasks
      .map((t) => `${t.name} ${(this.lastMs.get(t.name) ?? 0).toFixed(2)}`)
      .join("  ");
  }
}
